'use client'

import { Suspense } from 'react'
import { Analytics } from '@vercel/analytics/react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from 'sonner'
import { AutomergeProvider } from '@/document'
import { ErrorBoundary } from './error-boundary'
import ReactQueryProvider from './ReactQueryProvider'

/**
 * The root provider stack, out of `app/layout.tsx:142-164` (#317).
 *
 * The root layout kept this tree inline, which made a route file own composition — the thing
 * ADR 0010 says an adapter does not do. What stayed there is the part Next.js resolves by name or
 * by convention: `metadata`, `viewport`, the `next/font` declarations, and the `<html>`/`<body>`
 * document chrome around this.
 *
 * The order is load-bearing. `ErrorBoundary` is outermost so a provider that throws during mount
 * still renders a recovery screen rather than a blank document; `AutomergeProvider` is above
 * `Suspense` because the repo boot is what suspends.
 * [#111](https://github.com/cinjoff/konstantout/issues/111) is open on this stack shipping
 * `AutomergeAdapter` to all 13 routes.
 *
 * `Toaster`, `Analytics` and the devtools sit inside rather than beside `children`, as they did
 * before the move: the toaster is mounted by surfaces across four domains and must outlive a route
 * transition.
 */
export function RootProviders({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary>
      <AutomergeProvider>
        <Suspense fallback={null}>
          <ReactQueryProvider>
            {children}
            <Analytics />
            <Toaster
              position="top-right"
              richColors
              theme="dark"
              toastOptions={{
                style: {
                  background: 'var(--card)',
                  border: '1px solid var(--border)',
                  color: 'var(--card-foreground)',
                },
              }}
            />
            <ReactQueryDevtools initialIsOpen={false} />
          </ReactQueryProvider>
        </Suspense>
      </AutomergeProvider>
    </ErrorBoundary>
  )
}
